import './Search.css';
import CreateQuestion from '../Upload/Upload.js';
import { useEffect, useCallback, useState } from 'react';
import SERVER_URL from '../../../../env.js';
import Cookies from 'js-cookie';

function Search({ toggleNav }) {
    const [questions, setQuestions] = useState([])
    const [search, setSearch] = useState('')
    const [searchTopics, setSearchTopics] = useState('')
    const [loading, setLoading] = useState(true)

    // Edit modal state
    const [editingQuestion, setEditingQuestion] = useState(null)
    const [editText, setEditText] = useState('')
    const [editTopic, setEditTopic] = useState('')
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [saveError, setSaveError] = useState('')

    // Add modal state
    const [addModalOpen, setAddModalOpen] = useState(false)
    const [addText, setAddText] = useState('')
    const [addTopic, setAddTopic] = useState('')
    const [adding, setAdding] = useState(false)
    const [addError, setAddError] = useState('')

    const copyToClipboard = async (text) => {
        await navigator.clipboard.writeText(text);
    };

    const searchFilter = useCallback((question) => {
        if (!search && !searchTopics) {
            return true
        }

        if (search && !question?.question?.toLowerCase().includes(search?.toLowerCase())) {
            return false
        }

        if (searchTopics && !question?.topic?.toLowerCase().includes(searchTopics?.toLowerCase())) {
            return false
        }

        return true
    }, [search, searchTopics])

    const openEdit = (question) => {
        setEditingQuestion(question)
        setEditText(question.question)
        setEditTopic(question.topic || '')
        setSaveError('')
        setConfirmDelete(false)
    }

    const closeEdit = () => {
        setEditingQuestion(null)
        setSaveError('')
        setConfirmDelete(false)
    }

    const saveEdit = async () => {
        setSaving(true)
        setSaveError('')
        try {
            const response = await fetch(SERVER_URL + '/questions/' + editingQuestion.id, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + Cookies.get('accessToken'),
                },
                body: JSON.stringify({
                    question: editText,
                    topic: editTopic,
                }),
            })
            const data = await response.json()
            if (data.error) {
                setSaveError(data.error)
            } else {
                setQuestions((prev) =>
                    prev.map((q) => (q.id === data.question.id ? data.question : q))
                )
                closeEdit()
            }
        } catch (err) {
            setSaveError('An unexpected error occurred.')
        } finally {
            setSaving(false)
        }
    }

    const deleteQuestion = async () => {
        if (!confirmDelete) {
            setConfirmDelete(true)
            return
        }
        setDeleting(true)
        setSaveError('')
        try {
            const response = await fetch(SERVER_URL + '/questions/' + editingQuestion.id, {
                method: 'DELETE',
                headers: {
                    'Authorization': 'Bearer ' + Cookies.get('accessToken'),
                },
            })
            const data = await response.json()
            if (data.error) {
                setSaveError(data.error)
            } else {
                setQuestions((prev) => prev.filter((q) => q.id !== editingQuestion.id))
                closeEdit()
            }
        } catch (err) {
            setSaveError('An unexpected error occurred.')
        } finally {
            setDeleting(false)
        }
    }

    const openAddModal = () => {
        setAddModalOpen(true)
        setAddText('')
        setAddTopic('')
        setAddError('')
    }

    const closeCreate = () => {
        setAddModalOpen(false)
    }

    useEffect(() => {
        fetch(SERVER_URL + '/questions', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + Cookies.get('accessToken'),
            },
        })
            .then((response) => response.json())
            .then((data) => {
                if (!data.error) {
                    setQuestions(data.questions);
                }
            })
            .catch((error) => {
                console.error('Error fetching questions:', error);
            })
            .finally(() => {
                setLoading(false);
            })
    }, []);

    return (
        <>
            <div className="question-search-container">
                <input type="text" className="question-search-input" placeholder="Question..." onChange={(e) => setSearch(e.target.value)} />
                <input type="text" className="question-search-input" placeholder="Topic..." onChange={(e) => setSearchTopics(e.target.value)} />
            </div>
            <div className="qs-create-btn-container">
                <button className="qs-btn-add" onClick={openAddModal}>Add Question</button>
            </div>

            {loading ? (
                <div className="loading-container">
                    <div className="loading">Loading...</div>
                </div>
            ) : questions.length > 0 ? (
                <div className="question-container">
                    {questions.filter((question) => searchFilter(question)).map((question, index) => (
                        <div key={index} className='question-row'>
                            <div className='question-number'>#{index + 1}</div>
                            <div className='question-main-actions'>
                                <div className='question-main'>
                                    <div className='question-meta'>
                                        {question.topic && <span className="question-tag topic-tag">{question.topic}</span>}
                                    </div>
                                    <div className='question-text'>{question.question}</div>
                                </div>
                                <div className='question-actions'>
                                    <button className='modal-btn modal-btn-save' onClick={() => copyToClipboard(question.question)}>Copy</button>
                                    <button className='modal-btn modal-btn-cancel' onClick={() => openEdit(question)}>Edit</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="no-questions-container">
                    <div className="no-questions">No questions found.</div>
                </div>
            )}

            {editingQuestion && (
                <div className="modal-overlay" onClick={closeEdit}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="modal-title">Edit Question</h2>
                            <button className="modal-close" onClick={closeEdit}>&#x2715;</button>
                        </div>

                        <div className="modal-body">
                            <label className="modal-label">Topic</label>
                            <input
                                className="qs-modal-input qs-modal-text"
                                type="text"
                                value={editTopic}
                                onChange={(e) => setEditTopic(e.target.value)}
                                placeholder="Topic..."
                            />

                            <label className="modal-label">Question</label>
                            <textarea
                                className="qs-modal-input qs-modal-textarea"
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                                placeholder="Question..."
                            />

                            {saveError && <p className="modal-error">{saveError}</p>}
                        </div>

                        <div className="modal-footer">
                            <button
                                className={`modal-btn ${confirmDelete ? 'modal-btn-confirm-delete' : 'modal-btn-delete'}`}
                                onClick={deleteQuestion}
                                disabled={saving || deleting}
                            >
                                {deleting ? 'Deleting...' : confirmDelete ? 'Confirm Delete' : 'Delete'}
                            </button>
                            <div className="modal-footer-right">
                                <button className="modal-btn modal-btn-cancel" onClick={closeEdit} disabled={saving || deleting}>
                                    Cancel
                                </button>
                                <button className="modal-btn modal-btn-save" onClick={saveEdit} disabled={saving || deleting}>
                                    {saving ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {addModalOpen && (
                <div className="modal-overlay" onClick={closeCreate}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="modal-title">Add Question</h2>
                            <button className="modal-close" onClick={closeCreate}>&#x2715;</button>
                        </div>
                        <div className="modal-body">
                            <CreateQuestion closeCreate={closeCreate} setQuestions={setQuestions} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Search;
